import {
  setBookmarkedData,
  setBookmarkedCollectionIds,
  setCollectionId,
  setCollectionSourceId,
  setCollections,
  setParentCollections,
  setSummary,
  setReadingSummary,
  setLevel,
  setReadingLevelAvailable,
} from "context/features/dataSlice";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  addOrUpdateBookmark,
  createNewCollection,
  deleteBookmark,
  getCollections,
  getCollectionsSource,
  getParentCollections,
  saveCitation,
} from "../services/collection.service";
import { toast } from "react-toastify";
import { logEvent } from "services/amplitude.service";
import { useLocation } from "react-router-dom";

const useCollection = (
  setSelectedCollection,
  selectedStyle,
  authors,
  setmarkdownSummary,
  setmarkdownLevel,
  level,
  setOriginalSummary = () => { },
  setOriginalLevelSummary = () => { },
  isAIGeneratedContent = true,
  setIsAIGeneratedContent = () => { },
  isAIGeneratedLevel = true,
  setIsAIGeneratedLevel = () => { }
) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const ismisinformation = pathname.includes("/tools/dubious-sources-search");
  const isCollectionPage = pathname.startsWith("/collections/");

  const { user } = useSelector((state) => state.auth);
  const {
    collections,
    summaryInfoData,
    collectionSourceId,
    collectionId,
    selectedBookmarkOption,
    bookmarkedCollectionIds,
  } = useSelector((state) => state.data);

  const deleteBookmarkSource = async () => {
    try {
      // Only remove from the collection currently being viewed, unless it's the last one, in which case it's a full delete.
      const remainingIds = (bookmarkedCollectionIds || []).filter(
        (id) => Number(id) !== Number(collectionId)
      );
      const isLastCollection = !collectionId || remainingIds.length === 0;

      if (isLastCollection) {
        await deleteBookmark(user, collectionId, collectionSourceId);
        dispatch(
          setBookmarkedData({
            id: summaryInfoData.url,
            isCitationAvailable: false,
            isSummaryAvailable: false,
            collectionId: null,
            collectionSourceId: null,
          })
        );
        dispatch(setBookmarkedCollectionIds([]));
      } else {
        await saveBookmarkToCollections(remainingIds);
      }
      logEvent("deletePopup", { bookmark: collectionSourceId });

      toast.success("Bookmark Deleted Successfully!");
    } catch (e) {
      console.error(e);
    }
  };

  const getData = async () => {
    const data = await getCollections(user);

    let newArr = [];
    data.forEach((item) =>
      newArr.push({
        name: item.collectionName_chr,
        value: item.collectionName_chr,
        collectionId: item.collectionID_ids,
        parentCollectionId: item.parentCollectionID_lng,
        isShared_ysn: item.isShared_ysn,
        isPublic_ysn: item.isPublic_ysn,
      })
    );
    dispatch(setCollections(newArr));
  };

  const getBookmarkData = async () => {
    try {
      if (collectionSourceId) {
        const res = await getCollectionsSource(
          isCollectionPage ? collectionId : undefined,
          collectionSourceId
        );
        const data = res[0];
        console.log("data", data.isSummaryAIGeneratedContent_ysn);
        console.log("data", data.isLevellingAIGeneratedContent_ysn);
        if (data.isSummaryAIGeneratedContent_ysn) {
          setIsAIGeneratedContent(data.isSummaryAIGeneratedContent_ysn);
        }
        if (data.isLevellingAIGeneratedContent_ysn) {
          setIsAIGeneratedLevel(data.isLevellingAIGeneratedContent_ysn);
        }
        if (data.isSummaryAvailable_ysn)
          setmarkdownSummary(data.summary_chr);
        setOriginalSummary(data.summary_chr);
        if (data.isReadingLevelAvailable_ysn)
          setmarkdownLevel(data.articleComprehension_chr);
        setOriginalLevelSummary(data.articleComprehension_chr);
        dispatch(setReadingSummary(data.articleComprehension_chr));
        dispatch(setReadingLevelAvailable(data.isReadingLevelAvailable_ysn));
        if (data) {
          dispatch(setLevel(data.comprehensionLevel_chr));
        }
        // Only the Collections page needs this — its rows don't know the bookmark's full membership, unlike search/category/pulse cards, which already seeded it.
        if (isCollectionPage && data) {
          dispatch(
            setBookmarkedCollectionIds(
              (data.collectionMemberships || res)
                .map((item) => item.collectionID_lng)
                .filter(Boolean)
            )
          );
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchParentCollection = async () => {
    const data = await getParentCollections(user, 0);

    let newArr = [];
    data.forEach((item) =>
      newArr.push({
        name: item.collectionName_chr,
        value: item.collectionName_chr,
        collectionId: item.collectionID_ids,
        parentCollectionId: item.parentCollectionID_lng,
        isShared_ysn: item.isShared_ysn,
        isPublic_ysn: item.isPublic_ysn,
      })
    );
    dispatch(setParentCollections(newArr));
  };

  const saveNewCollection = async (name) => {
    try {
      const data = await createNewCollection(user, name, collectionId);

      const obj = {
        name: name,
        value: name,
        collectionId: data.collectionID_ids,
        parentCollectionId: collectionId,
      };
      const newCollections = [obj, ...collections];
      if (!collectionId) {
        dispatch(setParentCollections(newCollections));
      }
      dispatch(setCollectionId(data.collectionID_ids));
      setSelectedCollection(data.collectionID_ids);
      await getData();

      return data.collectionID_ids;
    } catch (e) {
      console.error(e);
    }
  };

  const createOrUpdateBookmark = async (
    markdownSummary,
    markdownLevel,
    levelLoaded = false,
    summaryLoaded = false
  ) => {
    const collectionID_ids = collectionId
      ? collectionId
      : collections[0].collectionId;

    // AddUpdateCollectionsSource treats collectionIds as the full membership, so it must be sent or the bookmark drops out of every other collection.
    const collectionIds = Array.from(
      new Set([...(bookmarkedCollectionIds || []), collectionID_ids])
    );

    const data = await addOrUpdateBookmark(
      user,
      summaryInfoData,
      markdownSummary,
      collectionID_ids,
      collectionSourceId,
      markdownLevel,
      levelLoaded,
      summaryLoaded,
      level,
      ismisinformation,
      isAIGeneratedContent,
      isAIGeneratedLevel,
      collectionIds
    );
    if (selectedBookmarkOption !== "summary" && selectedBookmarkOption !== "citation" && selectedBookmarkOption !== "readinglevel") {
      toast.success("Bookmark successfully saved to collection.");
    }
    dispatch(setCollectionSourceId(data.collectionSourceID_ids));
    dispatch(setBookmarkedCollectionIds(collectionIds));

    return data.collectionSourceID_ids;
  };

  const saveBookmarkToCollections = async (newIds) => {
    const primaryCollectionId = newIds[0] ?? collectionId;

    const data = await addOrUpdateBookmark(
      user,
      summaryInfoData,
      "",
      primaryCollectionId,
      collectionSourceId,
      false,
      false,
      false,
      null,
      ismisinformation,
      isAIGeneratedContent,
      isAIGeneratedLevel,
      newIds
    );
    dispatch(setCollectionId(primaryCollectionId));
    dispatch(setCollectionSourceId(data.collectionSourceID_ids));
    dispatch(setBookmarkedCollectionIds(newIds));
    dispatch(
      setBookmarkedData({
        id: summaryInfoData.url,
        collectionId: primaryCollectionId,
        collectionSourceId: data.collectionSourceID_ids,
        collectionMemberships: newIds.map((id) => ({
          collectionSourceID_lng: data.collectionSourceID_ids,
          collectionID_lng: id,
        })),
        switch: true,
      })
    );
    return data;
  };

  const toggleBookmarkCollection = async (targetCollectionId, checked) => {
    const currentIds = bookmarkedCollectionIds || [];
    const newIds = checked
      ? Array.from(new Set([...currentIds, targetCollectionId]))
      : currentIds.filter((cid) => cid !== targetCollectionId);
    return saveBookmarkToCollections(newIds);
  };

  const editBookmark = async (
    articleData,
    citation,
    markdownSummary,
    citationLoaded,
    levelLoaded,
    markdownLevel,
    summaryLoaded
  ) => {
    let id = collectionSourceId,
      collectionID_ids = collectionId;
    console.log("isAIGeneratedContent", isAIGeneratedContent);
    console.log("isAIGeneratedLevel", isAIGeneratedLevel);
    try {
      id = await createOrUpdateBookmark(
        markdownSummary,
        markdownLevel,
        levelLoaded,
        summaryLoaded
      );

      if (citationLoaded) {
        await saveCitation(
          articleData,
          id,
          user,
          citation,
          selectedStyle,
          authors
        );
      }
      if (!collectionId) {
        collectionID_ids = collections[0].collectionId;
        dispatch(setCollectionId(collections[0].collectionId));
      }
      dispatch(
        setBookmarkedData({
          id: summaryInfoData.url,
          isCitationAvailable: citationLoaded,
          isSummaryAvailable: markdownSummary.length > 0,
          collectionId: collectionID_ids,
          collectionSourceId: id,
        })
      );
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong, please try later.");
    }
  };

  useEffect(() => {
    getData();
    fetchParentCollection();
  }, []);

  useEffect(() => {
    if (isCollectionPage) {
      dispatch(setBookmarkedCollectionIds([]));
    }
    getBookmarkData();
  }, []);

  return {
    editBookmark,
    saveNewCollection,
    createOrUpdateBookmark,
    toggleBookmarkCollection,
    saveBookmarkToCollections,
    bookmarkedCollectionIds,
    deleteBookmarkSource,
  };
};

export default useCollection;
